/**
 * ReaderChrome — React-DOM top-bar + footer for the Arianna reader.
 *
 * Design source of truth:
 *   Top-bar: apps/reader/src/screens/ManualScreen.tsx (ReaderTopBar)
 *   Footer:  apps/reader/src/screens/ManualScreen.tsx (ReaderFooter)
 *
 * Props are fully platform-agnostic (no Inertia/Tauri deps). The host is
 * responsible for state (currentIdx, completedIds, reviewIds, tocOpen) and
 * must wire the callbacks.
 *
 * Usage pattern — wrap the content pane:
 *   <div className="flex flex-col h-dvh">
 *     <ReaderTopBar ... />
 *     <div className="flex-1 overflow-hidden">
 *       {children}
 *     </div>
 *     <ReaderFooter ... />
 *   </div>
 */

import * as React from 'react'
import { clsx } from 'clsx'
import type { ReaderRouteSection } from '@arianna/reader-core'
import Text from '@arianna/design-system/Text'
import { ThemePicker } from './ThemePicker.js'
import type { ThemeId } from './ThemePicker.js'

// ---------------------------------------------------------------------------
// Shared internal helper — truncate a nav label to the first N words.
// Extracted here so both ReaderTopBar and ReaderFooter can use it.
// ---------------------------------------------------------------------------
function truncateWords(title: string, maxWords = 3): string {
  const words = title.split(' ')
  if (words.length <= maxWords) return title
  return `${words.slice(0, maxWords).join(' ')}…`
}

// ---------------------------------------------------------------------------
// ReaderTopBar
// ---------------------------------------------------------------------------

export interface ReaderTopBarProps {
  /** Manual title shown in the serif heading area. */
  title: string
  /** Optional subtitle/author shown below the title (mirrors iPad GAP-10). */
  subtitle?: string | null
  /** Eyebrow label above the title (default: "Manuale"). */
  eyebrow?: string
  /** Progress pill label, e.g. "3/12". */
  progressLabel: string
  /** Whether the TOC toggle button is currently active (sidebar open). */
  tocOpen?: boolean
  /** Called when the user clicks the TOC toggle. */
  onToggleToc?: () => void
  /**
   * Called when the user clicks the "mark completed" icon button in the top-bar.
   * When omitted, the completion button is not rendered.
   */
  onToggleCompleted?: () => void
  /** Whether the current section is completed (drives the checkmark icon state). */
  isCompleted?: boolean
  /**
   * Optional inline prev/next navigation inside the top-bar (web platform
   * shows arrows + progress label after the right-action group).
   * When omitted, no inline navigation is rendered.
   */
  prevSection?: ReaderRouteSection | null
  nextSection?: ReaderRouteSection | null
  onPrev?: () => void
  onNext?: () => void
  /**
   * Current active theme. When both `currentTheme` and `onThemeChange` are
   * provided, the ThemePicker button is rendered in the right-action group.
   * Optional so that desktop can adopt later without breaking.
   */
  currentTheme?: ThemeId
  /**
   * Called when the user selects a different theme in the ThemePicker.
   * Must be paired with `currentTheme` — picker is hidden when either is absent.
   */
  onThemeChange?: (next: ThemeId) => void
  /** Optional additional CSS class for the root element. */
  className?: string
}

/**
 * Top application bar. Mirrors the iPad ReaderTopBar layout:
 *   [TOC toggle] [eyebrow + title + subtitle] [progress pill]
 *   [completed toggle] [theme] [separator] [prev icon] [next icon]
 *
 * TASK-014: product navigation (library, quiz, padronanza, tutor overlay,
 * "da rivedere" panel) moved to CourseTabs (apps/platform's course shell) —
 * removed from here. apps/platform no longer renders this bar at all (its
 * course shell header + ReaderFooter + TOC sidebar cover everything);
 * consumers today are apps/desktop and apps/reader-style layouts.
 *
 * Each action group is optional — omit props to hide the corresponding button.
 */
export function ReaderTopBar({
  title,
  subtitle,
  eyebrow = 'Manuale',
  progressLabel,
  tocOpen = false,
  onToggleToc,
  onToggleCompleted,
  isCompleted = false,
  currentTheme,
  onThemeChange,
  prevSection,
  nextSection,
  onPrev,
  onNext,
  className,
}: ReaderTopBarProps) {
  return (
    <div
      className={clsx(
        /* design handoff: 10px vertical padding — off-scale (between space-2=8px and space-3=12px); minHeight 54px singoletto */
        'flex items-center gap-3 py-[10px] px-5 min-h-[54px] border-b border-line bg-bg shrink-0',
        className,
      )}
    >
      {/* TOC toggle — design handoff: 36×36 icon button with 8px radius */}
      {onToggleToc != null && (
        <button
          onClick={onToggleToc}
          title="Indice"
          aria-pressed={tocOpen}
          className={clsx(
            'inline-flex items-center justify-center size-9 rounded-md border-none cursor-pointer text-ink-2 shrink-0',
            tocOpen ? 'bg-surface-2' : 'bg-transparent',
          )}
        >
          {/* Hamburger / TOC icon */}
          <svg
            width="20"
            height="20"
            viewBox="0 0 22 22"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.6"
            strokeLinecap="round"
            aria-hidden="true"
          >
            <line x1="4" y1="6" x2="18" y2="6" />
            <line x1="4" y1="11" x2="18" y2="11" />
            <line x1="4" y1="16" x2="13" y2="16" />
          </svg>
        </button>
      )}

      {/* Title block — eyebrow + title + optional subtitle (GAP-10). */}
      <div className="flex-1 min-w-0 ml-1">
        {/* design handoff: 10px eyebrow — below text-xs (11px); singoletto */}
        <div className="text-[10px] text-ink-3 font-sans font-semibold uppercase tracking-[0.12em]">
          {eyebrow}
        </div>
        {/* design handoff: 17px — text-lg in DS tokens; exact match */}
        <div className="text-lg tracking-[-0.015em] font-serif font-semibold text-ink truncate">
          {title}
        </div>
        {subtitle != null && subtitle.length > 0 && (
          /* design handoff: 11px — text-xs exact match in DS */
          <div className="text-xs text-ink-3 font-sans truncate">{subtitle}</div>
        )}
      </div>

      {/* Progress pill (mirrors iPad top-bar pill) */}
      {/* design handoff: 5px vertical / 10px horizontal padding, full radius; 11px = text-xs */}
      <div className="py-[5px] px-[10px] rounded-full bg-surface border border-line shrink-0">
        <Text variant="meta" className="text-xs text-ink-3 font-semibold">
          {progressLabel}
        </Text>
      </div>

      {/* Right actions */}
      <div className="flex items-center gap-1">
        {/* Completion toggle — design handoff: 36×36 icon button with 8px radius */}
        {onToggleCompleted != null && (
          <button
            onClick={onToggleCompleted}
            title={isCompleted ? 'Segna come da completare' : 'Segna come completato'}
            className={clsx(
              'inline-flex items-center justify-center size-9 rounded-md border-none cursor-pointer',
              isCompleted ? 'bg-surface-2 text-green' : 'bg-transparent text-ink-3',
            )}
          >
            <svg
              width="18"
              height="18"
              viewBox="0 0 22 22"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.8"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <polyline points="4 11 9 16 18 6" />
            </svg>
          </button>
        )}

        {/* Theme picker — rendered only when both props are provided (web platform).
            Placed after the completion toggle and before the optional inline nav separator. */}
        {currentTheme != null && onThemeChange != null && (
          <ThemePicker currentTheme={currentTheme} onChange={onThemeChange} />
        )}

        {/* Inline prev/next navigation (optional — web platform uses this) */}
        {(onPrev != null || onNext != null) && (
          <>
            {/* design handoff: 1×22px separator line */}
            <div className="w-px h-[22px] bg-line mx-2" />
            {/* design handoff: 32×32 smaller nav button with 6px radius */}
            <button
              disabled={prevSection == null}
              onClick={onPrev}
              title="Paragrafo precedente"
              className={clsx(
                'inline-flex items-center justify-center size-8 rounded-[6px] bg-transparent border-none',
                prevSection == null ? 'cursor-default text-ink-4' : 'cursor-pointer text-ink-2',
              )}
            >
              <svg
                width="16"
                height="16"
                viewBox="0 0 16 16"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.8"
                strokeLinecap="round"
                aria-hidden="true"
              >
                <polyline points="10 3 5 8 10 13" />
              </svg>
            </button>
            {/* design handoff: 11px mono label — text-xs exact match in DS */}
            <span className="text-xs min-w-[40px] font-mono text-ink-3 text-center">
              {progressLabel}
            </span>
            {/* design handoff: 32×32 smaller nav button with 6px radius */}
            <button
              disabled={nextSection == null}
              onClick={onNext}
              title="Paragrafo successivo"
              className={clsx(
                'inline-flex items-center justify-center size-8 rounded-[6px] bg-transparent border-none',
                nextSection == null ? 'cursor-default text-ink-4' : 'cursor-pointer text-ink-2',
              )}
            >
              <svg
                width="16"
                height="16"
                viewBox="0 0 16 16"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.8"
                strokeLinecap="round"
                aria-hidden="true"
              >
                <polyline points="6 3 11 8 6 13" />
              </svg>
            </button>
          </>
        )}
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// ReaderFooter
// ---------------------------------------------------------------------------

// truncateTitle is now an alias of the shared truncateWords helper above.
const truncateTitle = truncateWords

export interface ReaderFooterProps {
  /** Section immediately before currentIdx (null when at the start). */
  prevSection: ReaderRouteSection | null
  /** Section immediately after currentIdx (null when at the end). */
  nextSection: ReaderRouteSection | null
  /** Whether the current section is marked as completed. */
  isCompleted: boolean
  /** Whether the current section is flagged for review. */
  isReview: boolean
  /** Called when the user presses the Prev button. */
  onPrev: () => void
  /** Called when the user presses the Next button. */
  onNext: () => void
  /** Called when the user toggles the completion state. */
  onToggleCompleted: () => void
  /** Called when the user toggles the review flag. */
  onToggleReview: () => void
  /** Optional additional CSS class for the root element. */
  className?: string
}

/**
 * Bottom navigation bar. Mirrors the iPad ReaderFooter layout:
 *   [← Prev]  [spacer]  |  [⚑ Da rivedere] [✓ Completato]  |  [spacer]  [Next →]
 * Prev is pinned to the left edge, Next to the right edge; the review/
 * completed state cluster is centered between two quiet dividers.
 */
export function ReaderFooter({
  prevSection,
  nextSection,
  isCompleted,
  isReview,
  onPrev,
  onNext,
  onToggleCompleted,
  onToggleReview,
  className,
}: ReaderFooterProps) {
  const prevNumber =
    prevSection != null && prevSection.parentKind !== 'compendio' ? prevSection.id : null
  const nextNumber =
    nextSection != null && nextSection.parentKind !== 'compendio' ? nextSection.id : null

  return (
    <div
      className={clsx(
        'border-t border-line bg-bg flex items-center gap-2 py-3 px-5 shrink-0 flex-wrap',
        className,
      )}
    >
      {/* Prev — pinned to the left edge. Design handoff: 8px/12px padding,
          8px radius, 13px font, 180px max. Arrow + chapter number stay
          shrink-0 (always visible); only the title truncates. */}
      <button
        disabled={prevSection == null}
        onClick={onPrev}
        className={clsx(
          'inline-flex items-center gap-1.5 py-2 px-3 rounded-md text-sm max-w-[180px] bg-surface border border-line font-sans',
          prevSection == null
            ? 'cursor-default text-ink-4 opacity-[0.42]'
            : 'cursor-pointer text-ink-2',
        )}
      >
        <span className="shrink-0 text-ink-4">←</span>
        {prevNumber != null && (
          <span className="shrink-0 text-gold font-semibold tabular-nums">{prevNumber}</span>
        )}
        <span className="min-w-0 overflow-hidden text-ellipsis whitespace-nowrap">
          {prevSection ? truncateTitle(prevSection.node.title) : 'Inizio'}
        </span>
      </button>

      <div className="flex-1" />

      {/* Quiet group divider separating navigation (prev, left) from the
          chapter state cluster (review/completed, centered). */}
      <div className="w-px self-stretch bg-line mx-1" />

      {/* Review toggle (mirrors iPad ReviewButton / ReaderFooter) */}
      {/* design handoff: 8px/12px padding, 8px radius, 13px font;
          border and background are dynamic (color-mix based on isReview) — kept inline */}
      <button
        onClick={onToggleReview}
        aria-pressed={isReview}
        title={isReview ? 'Rimuovi dal ripasso' : 'Aggiungi al ripasso'}
        className="py-2 px-3 rounded-md text-sm text-gold font-sans font-medium cursor-pointer"
        style={{
          border: `1px solid ${isReview ? 'var(--gold)' : 'color-mix(in oklab, var(--gold) 40%, transparent)'}`,
          background: isReview
            ? 'color-mix(in oklab, var(--gold) 12%, transparent)'
            : 'var(--surface)',
        }}
      >
        {isReview ? '⚑ Segnato da rivedere' : '⚑ Da rivedere'}
      </button>

      {/* Completion toggle — design handoff: 9px/16px padding, 9px radius, 13px font */}
      <button
        onClick={onToggleCompleted}
        title={isCompleted ? 'Segna come da completare' : 'Segna come completato'}
        className={clsx(
          'py-[9px] px-4 rounded-[9px] text-sm border text-bg font-sans font-semibold cursor-pointer',
          isCompleted ? 'bg-green border-green' : 'bg-ink border-ink',
        )}
      >
        {isCompleted ? '✓ Completato · annulla' : '✓ Segna completato'}
      </button>

      {/* Quiet group divider separating the state cluster (centered) from
          navigation (next, right). */}
      <div className="w-px self-stretch bg-line mx-1" />

      <div className="flex-1" />

      {/* Next — pinned to the right edge. Design handoff: 8px/12px padding,
          8px radius, 13px font, 180px max. Chapter number + arrow stay
          shrink-0 (always visible); only the title truncates. */}
      <button
        disabled={nextSection == null}
        onClick={onNext}
        className={clsx(
          'inline-flex items-center gap-1.5 py-2 px-3 rounded-md text-sm max-w-[180px] bg-surface border border-line font-sans',
          nextSection == null
            ? 'cursor-default text-ink-4 opacity-[0.42]'
            : 'cursor-pointer text-ink-2',
        )}
      >
        {nextNumber != null && (
          <span className="shrink-0 text-gold font-semibold tabular-nums">{nextNumber}</span>
        )}
        <span className="min-w-0 overflow-hidden text-ellipsis whitespace-nowrap">
          {nextSection ? truncateTitle(nextSection.node.title) : 'Fine'}
        </span>
        <span className="shrink-0 text-ink-4">→</span>
      </button>
    </div>
  )
}
